import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Trash2 } from "lucide-react";
import { useMarketplacePending } from "@/api/queries/plugins";
import { useApproveDraft, useDiscardDraft } from "@/api/mutations/repoLifecycle";
import { feldAenderungLabel, repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import { PendingChangeRow } from "@/components/repo-lifecycle/PendingChangeRow";
import { PluginApproveDialog } from "@/components/plugin-catalog/PluginApproveDialog";
import { UpdateAgentsNachFreigabeDialog } from "@/components/plugin-catalog/UpdateAgentsNachFreigabeDialog";
import { FreischaltenNachFreigabeDialog } from "@/components/plugin-catalog/FreischaltenNachFreigabeDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { ConfirmDialog } from "@/components/ConfirmDialog";

// #291/#292/#293: „Wartet auf Freigabe" — die Arbeitsliste des Katalogs, KOMPLETT in
// Fachobjekt-Sprache (Standing Rule: UI zeigt Fachobjekte, Git ist Speicher):
//   * Plugin wird AUFGENOMMEN  → Zeile mit Name-Link aufs Plugin-Detail + Freigeben/Verwerfen
//   * Plugin wird GEÄNDERT/ENTFERNT (#293) → „Änderung an X" / „X wird entfernt", ebenso
//   * Änderung am KATALOG selbst (kein Plugin-Bezug) → PendingChangeRow mit Diff-DIALOG
// Kein „Ansehen"-Panel-Umschalten, keine Branch-Namen — die Lifecycle-Koordinaten sind
// reine Adresse für die Mutationen.
export function PendingSection({ marketplaceId }: { marketplaceId: string }) {
  const pending = useMarketplacePending(marketplaceId);
  const approve = useApproveDraft();
  const discard = useDiscardDraft();
  const [searchParams, setSearchParams] = useSearchParams();
  const [verwerfen, setVerwerfen] = useState<string | null>(null); // draftBranch
  // Review-Fund 7: nach der Freigabe eines Katalog-Entwurfs schließt derselbe
  // Verkettungs-Dialog an wie in der LifecycleBar (#288 UC-6) — zwei Freigabe-Wege,
  // EIN Folgeablauf.
  const [freischaltenOpen, setFreischaltenOpen] = useState(false);
  // #294 (UC-0): Freigeben läuft über den Versions-Dialog; (UC-4) nach einer
  // ÄNDERUNGS-Freigabe schließt das Ausrollen an.
  const [approveTarget, setApproveTarget] = useState<{ name: string; branch: string; art: "aufnahme" | "aenderung" } | null>(null);
  const [ausrollenName, setAusrollenName] = useState<string | null>(null);

  // Review-Fund 3 (falsches Grün): ein Ladefehler der Arbeitsliste darf nicht wie
  // „nichts wartet" aussehen.
  if (pending.isError) {
    return (
      <p className="text-[12.5px] text-danger" data-testid="pending-error">
        Freigabe-Arbeitsliste konnte nicht geladen werden.
      </p>
    );
  }

  const p = pending.data;
  if (!p || !p.manageable || p.drafts.length === 0) return null;
  if (p.repoId === null) return null;
  // nach dem Guard als plain string festhalten — TS trägt das Narrowing nicht in
  // gehoistete Funktionen (freigeben/onConfirm) hinein.
  const repoId: string = p.repoId;

  // #293: jede Zeile hängt am Fachobjekt. Aufnahmen + Änderungen sind Plugin-Zeilen;
  // was übrig bleibt, ist eine Änderung am KATALOG selbst — trotzdem listen, sonst wäre
  // „N Entwürfe offen" nicht nachvollziehbar.
  const geaendert = p.pluginsGeaendert ?? [];
  const zugeordnet = new Set([
    ...p.pluginsInAufnahme.map((x) => x.draftBranch),
    ...geaendert.map((x) => x.draftBranch),
  ]);
  const sonstige = p.drafts.filter((d) => !zugeordnet.has(d.branch));

  // Review-Fund 1: nach Freigeben/Verwerfen existiert der Branch nicht mehr — zeigt die
  // URL noch darauf, liefe das eingebettete Panel in „Konnte Verzeichnis nicht laden".
  // Zyklus-2-Fund: VOR dem setSearchParams prüfen — react-router pusht sonst auch bei
  // unveränderten Params einen sinnlosen History-Eintrag.
  function refAufLive(branch: string) {
    if (searchParams.get("ref") !== branch) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("ref");
      next.delete("file");
      next.delete("path");
      return next;
    });
  }

  // #294 (UC-0): Aufnahme/Änderung → Versions-Dialog (Plugin-Version = Update-Signal).
  // Entfernung bleibt Direkt-Freigabe (eine Version wäre dort sinnlos).
  function freigeben(name: string, branch: string, art: "aufnahme" | "aenderung" | "entfernung") {
    if (art !== "entfernung") {
      setApproveTarget({ name, branch, art });
      return;
    }
    approve.mutate(
      { repoId, branch },
      {
        onSuccess: () => {
          toast.success(`„${name}" ist aus dem Katalog entfernt.`);
          refAufLive(branch);
        },
        // Review-Fund 2: der Fach-Fehlertext (Vier-Augen, Schema) PLUS der Weg nach
        // vorn — der Override-Dialog und die Fundstellen liegen im Panel.
        onError: (err) =>
          toast.error(`${repoErrorMessage(err, "Freigeben fehlgeschlagen")} — Plugin-Detail öffnen und dort fortsetzen.`),
      },
    );
  }

  // Review-Fund (R1/5): „Verwerfen" trifft den ganzen ENTWURF — der Dialog benennt alle
  // Plugins, die daran hängen, nicht nur das angeklickte.
  const verwerfenNamen = verwerfen
    ? p.pluginsInAufnahme.filter((x) => x.draftBranch === verwerfen).map((x) => x.name)
    : [];
  const verwerfenAenderungen = verwerfen
    ? geaendert.filter((x) => x.draftBranch === verwerfen).map((x) => x.name)
    : [];

  return (
    <SectionCard
      icon={<ShieldCheck className="h-[18px] w-[18px]" />}
      title={`Wartet auf Freigabe (${p.drafts.length})`}
      description="Offene Entwürfe dieses Katalogs — Plugins erscheinen erst nach der Freigabe im Katalog."
      testId="pending-section"
    >
      {!p.liveReadable && (
        <p className="mb-2 text-[12.5px] text-warn">
          Der Live-Katalog ist gerade nicht lesbar — welche Einträge NEU sind, lässt sich nicht sagen. Die Entwürfe selbst sind unten gelistet.
        </p>
      )}
      {/* Review-Fund (R1/4): nicht lesbare ENTWURFS-Manifeste benennen — sie stünden
          sonst unbenannt als „sonstige Entwürfe" da. */}
      {p.draftsUnreadable > 0 && (
        <p className="mb-2 text-[12.5px] text-warn" data-testid="pending-unreadable">
          {p.draftsUnreadable === 1 ? "1 Entwurfs-Manifest war" : `${p.draftsUnreadable} Entwurfs-Manifeste waren`} nicht lesbar — diese Entwürfe erscheinen unten ohne Plugin-Zuordnung.
        </p>
      )}
      <ul className="space-y-1.5" data-testid="pending-plugins">
        {p.pluginsInAufnahme.map((x) => (
          <li key={x.name} className="flex items-center gap-3 rounded-md border border-border px-2.5 py-2" data-testid={`pending-plugin-${x.name}`}>
            {/* #292 (UC-1): auch ein in-Aufnahme-Plugin hat seine Detail-Seite — derselbe
                Lebenslauf-Ort wie nach der Freigabe. */}
            <Link
              to={`/plugins/marketplaces/${encodeURIComponent(marketplaceId)}/${encodeURIComponent(x.name)}`}
              className="font-medium text-accent-text hover:underline"
              data-testid={`pending-detail-${x.name}`}
            >
              {x.name}
            </Link>
            {x.description && <span className="truncate text-[11.5px] text-text3">{x.description}</span>}
            <Badge variant="busy">{x.draftState === "submitted" ? "wartet auf Freigabe" : "Entwurf"}</Badge>
            {/* Review-Fund 6: im Zustand „Entwurf" gibt es hier bewusst keinen
                Freigeben-Knopf — der Weg nach vorn steht dran statt zu fehlen. */}
            {x.draftState === "draft" && (
              <span className="text-[11.5px] text-text3">noch nicht eingereicht — im Plugin-Detail einreichen</span>
            )}
            <span className="ml-auto flex items-center gap-1.5">
              {/* #292 (Arbens Live-Fund): KEIN „Ansehen" mehr — Ansehen/Bearbeiten IST das
                  Plugin-Detail (der Name verlinkt dorthin, das Panel startet dort auf dem
                  Entwurf). Ein zweiter Anzeige-Ort im Marketplace-Panel war doppelt. */}
              {x.draftState === "submitted" && x.canApprove && (
                <Button
                  size="sm"
                  // Review-Fund 8: nur die ANGEKLICKTE Zeile sperren, nicht alle.
                  disabled={approve.isPending && approve.variables?.branch === x.draftBranch}
                  onClick={() => freigeben(x.name, x.draftBranch, "aufnahme")}
                  data-testid={`pending-approve-${x.name}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Freigeben
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={discard.isPending && discard.variables?.branch === x.draftBranch}
                onClick={() => setVerwerfen(x.draftBranch)}
                data-testid={`pending-discard-${x.name}`}
              >
                <Trash2 className="h-3.5 w-3.5 text-danger" />
              </Button>
            </span>
          </li>
        ))}
        {/* #293 (UC-2): Änderungen/Entfernungen an BESTEHENDEN Plugins — dieselbe
            Fachobjekt-Zeile wie die Aufnahme, Name verlinkt aufs Plugin-Detail. */}
        {geaendert.map((x) => (
          <li key={`${x.name}-${x.art}`} className="flex items-center gap-3 rounded-md border border-border px-2.5 py-2" data-testid={`pending-change-${x.name}`}>
            <Link
              to={`/plugins/marketplaces/${encodeURIComponent(marketplaceId)}/${encodeURIComponent(x.name)}`}
              className="font-medium text-accent-text hover:underline"
            >
              {x.art === "entfernung" ? `„${x.name}" wird entfernt` : `Änderung an „${x.name}"`}
            </Link>
            {x.art === "aenderung" && x.felder && (
              <span className="truncate text-[11.5px] text-text3">{feldAenderungLabel(x.felder)}</span>
            )}
            <Badge variant={x.art === "entfernung" ? "destructive" : "busy"}>
              {x.draftState === "submitted" ? "wartet auf Freigabe" : "noch nicht eingereicht"}
            </Badge>
            <span className="ml-auto flex items-center gap-1.5">
              {x.draftState === "submitted" && x.canApprove && (
                <Button
                  size="sm"
                  disabled={approve.isPending && approve.variables?.branch === x.draftBranch}
                  onClick={() => freigeben(x.name, x.draftBranch, x.art)}
                  data-testid={`pending-change-approve-${x.name}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Freigeben
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={discard.isPending && discard.variables?.branch === x.draftBranch}
                onClick={() => setVerwerfen(x.draftBranch)}
                data-testid={`pending-change-discard-${x.name}`}
              >
                <Trash2 className="h-3.5 w-3.5 text-danger" />
              </Button>
            </span>
          </li>
        ))}
        {/* #293 (UC-3): was übrig bleibt, ändert den KATALOG selbst — Entscheidung in der
            Zeile, „Änderungen ansehen" als Dialog (kein Panel-Umschalten, kein Branch). */}
        {sonstige.map((d) => (
          <PendingChangeRow
            key={d.branch}
            repoId={repoId}
            branch={d.branch}
            title={`Änderung am Katalog: ${d.title}`}
            state={d.state}
            createdBy={d.createdBy}
            canApprove={d.canApprove}
            discardDescription={`„${d.title}" wird verworfen — der Katalog bleibt unverändert. Das lässt sich nicht rückgängig machen.`}
            onDecided={() => refAufLive(d.branch)}
            testId={`pending-katalog-${d.slug}`}
          />
        ))}
      </ul>

      <ConfirmDialog
        open={!!verwerfen}
        onOpenChange={(o) => !o && setVerwerfen(null)}
        title="Entwurf verwerfen"
        description={
          // Review M3: ein Entwurf kann Aufnahmen UND Änderungen tragen — der Dialog
          // benennt ALLES, was mit ihm verworfen wird.
          [
            verwerfenNamen.length > 0
              ? verwerfenNamen.length > 1
                ? `Dieser Entwurf nimmt ${verwerfenNamen.length} Plugins auf (${verwerfenNamen.join(", ")}) — ALLE Aufnahmen werden verworfen (die Plugins erscheinen nicht im Katalog; bereits angelegte eigene Plugin-Repositories bleiben bestehen, danach unter Repositories → „Nicht zugeordnet").`
                : `Die Aufnahme von "${verwerfenNamen[0]}" wird verworfen — das Plugin erscheint nicht im Katalog (ein bereits angelegtes eigenes Plugin-Repository bleibt bestehen, danach unter Repositories → „Nicht zugeordnet").`
              : null,
            verwerfenAenderungen.length > 0
              ? `Die ausstehende Änderung an ${verwerfenAenderungen.map((n) => `"${n}"`).join(", ")} wird verworfen — der Katalog bleibt unverändert.`
              : null,
          ]
            .filter(Boolean)
            .join(" ") + " Das lässt sich nicht rückgängig machen."
        }
        confirmText="Verwerfen"
        confirmVariant="destructive"
        isPending={discard.isPending}
        onConfirm={() => {
          if (!verwerfen) return;
          const branch = verwerfen;
          discard.mutate(
            { repoId, branch },
            {
              onSuccess: () => {
                toast.success("Entwurf verworfen");
                refAufLive(branch);
                setVerwerfen(null);
              },
              onError: (err) => toast.error(repoErrorMessage(err, "Verwerfen fehlgeschlagen")),
            },
          );
        }}
      />

      {freischaltenOpen && (
        <FreischaltenNachFreigabeDialog marketplaceId={marketplaceId} open onOpenChange={setFreischaltenOpen} />
      )}

      {/* #294 (UC-0): Freigeben mit optionaler Plugin-Version; Verkettung danach:
          Aufnahme → Freischalten (#288), Änderung → Ausrollen (UC-4). */}
      {approveTarget && (
        <PluginApproveDialog
          open
          onOpenChange={(o) => !o && setApproveTarget(null)}
          marketplaceId={marketplaceId}
          pluginName={approveTarget.name}
          repoId={repoId}
          branch={approveTarget.branch}
          art={approveTarget.art}
          onApproved={() => {
            refAufLive(approveTarget.branch);
            if (approveTarget.art === "aufnahme") setFreischaltenOpen(true);
            else setAusrollenName(approveTarget.name);
            setApproveTarget(null);
          }}
        />
      )}

      {ausrollenName && (
        <UpdateAgentsNachFreigabeDialog
          open
          onOpenChange={(o) => !o && setAusrollenName(null)}
          marketplaceId={marketplaceId}
          pluginName={ausrollenName}
        />
      )}
    </SectionCard>
  );
}
